// src/pages/FeedbackPage.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { submitFeedback } from '../services/feedback';
import {
  BugAntIcon,
  LightBulbIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import styles from '../components/enquiries/Enquiries.module.css';

const FeedbackPage = () => {
  const { jeweller } = useAuth();
  const [activeTab, setActiveTab] = useState('bug'); // 'bug' or 'suggestion'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Bug report form state
  const [bugForm, setBugForm] = useState({
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    pageFeature: '',
    browser: '',
  });

  // Suggestion form state
  const [suggestionForm, setSuggestionForm] = useState({
    title: '',
    description: '',
    category: 'feature',
    priority: 'medium',
  });

  // Reset forms
  const resetBugForm = () => {
    setBugForm({
      title: '',
      description: '',
      stepsToReproduce: '',
      expectedBehavior: '',
      actualBehavior: '',
      pageFeature: '',
      browser: '',
    });
  };

  const resetSuggestionForm = () => {
    setSuggestionForm({
      title: '',
      description: '',
      category: 'feature',
      priority: 'medium',
    });
  };

  // Handle bug report submission
  const handleBugSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await submitFeedback({
        type: 'bug',
        ...bugForm,
        userId: jeweller?.id || 'unknown',
        companyName: jeweller?.companyName || 'Unknown Company',
        email: jeweller?.email || 'no-email@provided.com'
      });

      setSuccess(true);
      resetBugForm();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 10000);
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError(err.message || 'Failed to submit bug report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle suggestion submission
  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await submitFeedback({
        type: 'suggestion',
        ...suggestionForm,
        userId: jeweller?.id || 'unknown',
        companyName: jeweller?.companyName || 'Unknown Company',
        email: jeweller?.email || 'no-email@provided.com'
      });

      setSuccess(true);
      resetSuggestionForm();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      setError(err.message || 'Failed to submit suggestion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header matching Enquiries design */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1 className={styles.headerTitle}>Feedback & Support</h1>
            <p className={styles.headerSubtitle}>
              Help us improve by reporting bugs or suggesting new features. Use the form multiple times as needed.
            </p>
          </div>
        </div>
      </div>

      {/* Success Alert */}
      {success && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--spacing-s)',
          padding: 'var(--spacing-m)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--spacing-l)',
          background: 'var(--Green-50)',
          border: '1px solid var(--Green-200)',
          color: 'var(--Green-800)'
        }}>
          <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--Green-600)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--font-size-body3)', fontWeight: 'var(--font-weight-semibold)', margin: '0 0 var(--spacing-xxs) 0' }}>
              Submitted Successfully!
            </p>
            <p style={{ fontSize: 'var(--font-size-body4)', margin: 0 }}>
              Thank you for your feedback. We'll review it and be in touch if we have any questions.
            </p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--spacing-s)',
          padding: 'var(--spacing-m)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--spacing-l)',
          background: 'var(--Red-50)',
          border: '1px solid var(--Red-200)',
          color: 'var(--Red-800)'
        }}>
          <ExclamationTriangleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--Red-600)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--font-size-body3)', fontWeight: 'var(--font-weight-semibold)', margin: '0 0 var(--spacing-xxs) 0' }}>
              Submission Failed
            </p>
            <p style={{ fontSize: 'var(--font-size-body4)', margin: 0 }}>
              {error}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 'var(--spacing-m)',
        marginBottom: 'var(--spacing-l)'
      }}>
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-m)',
            padding: 'var(--spacing-l)',
            background: activeTab === 'bug' ? 'var(--Purple-50)' : 'var(--Surface-Primary)',
            border: activeTab === 'bug' ? '2px solid var(--Purple-600)' : '2px solid var(--Stroke-Light)',
            borderRadius: 'var(--radius-xl)',
            cursor: 'pointer',
            transition: 'var(--transition-all)',
            textAlign: 'left',
            boxShadow: activeTab === 'bug' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
          }}
          onClick={() => setActiveTab('bug')}
          onMouseEnter={(e) => !activeTab === 'bug' && (e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
          onMouseLeave={(e) => !activeTab === 'bug' && (e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
        >
          <BugAntIcon style={{ width: '2.5rem', height: '2.5rem', color: activeTab === 'bug' ? 'var(--Purple-600)' : 'var(--Text-Secondary)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 'var(--font-size-body2)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--Text-Primary)', marginBottom: 'var(--spacing-xxs)' }}>
              Report a Bug
            </div>
            <div style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)' }}>
              Something isn't working
            </div>
          </div>
        </button>

        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-m)',
            padding: 'var(--spacing-l)',
            background: activeTab === 'suggestion' ? 'var(--Purple-50)' : 'var(--Surface-Primary)',
            border: activeTab === 'suggestion' ? '2px solid var(--Purple-600)' : '2px solid var(--Stroke-Light)',
            borderRadius: 'var(--radius-xl)',
            cursor: 'pointer',
            transition: 'var(--transition-all)',
            textAlign: 'left',
            boxShadow: activeTab === 'suggestion' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
          }}
          onClick={() => setActiveTab('suggestion')}
          onMouseEnter={(e) => activeTab !== 'suggestion' && (e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
          onMouseLeave={(e) => activeTab !== 'suggestion' && (e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
        >
          <LightBulbIcon style={{ width: '2.5rem', height: '2.5rem', color: activeTab === 'suggestion' ? 'var(--Purple-600)' : 'var(--Text-Secondary)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 'var(--font-size-body2)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--Text-Primary)', marginBottom: 'var(--spacing-xxs)' }}>
              Make a Suggestion
            </div>
            <div style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)' }}>
              Idea for improvement
            </div>
          </div>
        </button>
      </div>

      {/* Bug Report Form */}
      {activeTab === 'bug' && (
        <div style={{
          background: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xl)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)'
        }}>
          <form onSubmit={handleBugSubmit}>
            {/* Form Title */}
            <h3 style={{
              fontSize: 'var(--font-size-subtitle2)',
              fontWeight: 'var(--font-weight-subtitle2)',
              color: 'var(--Text-Primary)',
              margin: '0 0 var(--spacing-xl) 0',
              paddingBottom: 'var(--spacing-m)',
              borderBottom: '2px solid var(--Stroke-Light)'
            }}>
              Bug Details
            </h3>
            
            {/* Bug Title */}
            <div style={{ marginBottom: 'var(--spacing-l)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Bug Title <span style={{ color: 'var(--Red-600)' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Brief description of the issue"
                value={bugForm.title}
                onChange={(e) => setBugForm({ ...bugForm, title: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-s)',
                  fontSize: 'var(--font-size-body3)',
                  fontFamily: 'var(--font-family-primary)',
                  color: 'var(--Text-Primary)',
                  background: 'var(--Surface-Primary)',
                  border: '1px solid var(--Stroke-Secondary)',
                  borderRadius: 'var(--radius-s)',
                  transition: 'var(--transition-all)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--Purple-500)';
                  e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--Stroke-Secondary)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Detailed Description */}
            <div style={{ marginBottom: 'var(--spacing-l)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Detailed Description <span style={{ color: 'var(--Red-600)' }}>*</span>
              </label>
              <textarea
                placeholder="Explain what happened in detail"
                rows={5}
                value={bugForm.description}
                onChange={(e) => setBugForm({ ...bugForm, description: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-s)',
                  fontSize: 'var(--font-size-body3)',
                  fontFamily: 'var(--font-family-primary)',
                  color: 'var(--Text-Primary)',
                  background: 'var(--Surface-Primary)',
                  border: '1px solid var(--Stroke-Secondary)',
                  borderRadius: 'var(--radius-s)',
                  transition: 'var(--transition-all)',
                  resize: 'vertical',
                  minHeight: '100px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--Purple-500)';
                  e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--Stroke-Secondary)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Steps to Reproduce */}
            <div style={{ marginBottom: 'var(--spacing-l)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Steps to Reproduce (optional)
              </label>
              <textarea
                placeholder="1. First I...&#10;2. Then I...&#10;3. Finally..."
                rows={4}
                value={bugForm.stepsToReproduce}
                onChange={(e) => setBugForm({ ...bugForm, stepsToReproduce: e.target.value })}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-s)',
                  fontSize: 'var(--font-size-body3)',
                  fontFamily: 'var(--font-family-primary)',
                  color: 'var(--Text-Primary)',
                  background: 'var(--Surface-Primary)',
                  border: '1px solid var(--Stroke-Secondary)',
                  borderRadius: 'var(--radius-s)',
                  transition: 'var(--transition-all)',
                  resize: 'vertical'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--Purple-500)';
                  e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--Stroke-Secondary)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Tertiary)',
                marginTop: 'var(--spacing-xs)',
                marginBottom: 0
              }}>
                Help us understand how to recreate the issue
              </p>
            </div>

            {/* Two Column Row - Expected/Actual */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-l)',
              marginBottom: 'var(--spacing-l)'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Expected Behavior (optional)
                </label>
                <input
                  type="text"
                  placeholder="What should happen?"
                  value={bugForm.expectedBehavior}
                  onChange={(e) => setBugForm({ ...bugForm, expectedBehavior: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Actual Behavior (optional)
                </label>
                <input
                  type="text"
                  placeholder="What actually happened?"
                  value={bugForm.actualBehavior}
                  onChange={(e) => setBugForm({ ...bugForm, actualBehavior: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Two Column Row - Page/Browser */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-l)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Page/Feature (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., Projects page, Profile editor"
                  value={bugForm.pageFeature}
                  onChange={(e) => setBugForm({ ...bugForm, pageFeature: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Browser/Device (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., Chrome on Mac, Safari on iPhone"
                  value={bugForm.browser}
                  onChange={(e) => setBugForm({ ...bugForm, browser: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* User Info Display */}
            <div style={{
              background: 'var(--Grey-50)',
              border: '1px solid var(--Stroke-Light)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-l)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Secondary)',
                margin: '0 0 var(--spacing-s) 0',
                textTransform: 'uppercase',
                letterSpacing: 'var(--letter-spacing-wide)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
                Submitting as:
              </p>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                margin: '0 0 var(--spacing-xxs) 0'
              }}>
                {jeweller?.companyName || 'Unknown Company'}
              </p>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                margin: 0
              }}>
                {jeweller?.email || 'No email'}
              </p>
            </div>

            {/* Submit Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--spacing-m)',
              paddingTop: 'var(--spacing-l)',
              borderTop: '1px solid var(--Stroke-Light)'
            }}>
              <button
                type="button"
                onClick={resetBugForm}
                disabled={loading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-s) var(--spacing-xl)',
                  fontSize: 'var(--font-size-button)',
                  fontWeight: 'var(--button-font-weight)',
                  fontFamily: 'var(--font-family-primary)',
                  borderRadius: 'var(--button-radius)',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-all)',
                  boxShadow: 'var(--shadow-sm)',
                  background: 'var(--Button-Secondary-Enabled-BG)',
                  color: 'var(--Button-Secondary-Text)',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'var(--Button-Secondary-Hovered-BG)', e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'var(--Button-Secondary-Enabled-BG)', e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
              >
                Clear Form
              </button>
              <button
                type="submit"
                disabled={loading || !bugForm.title || !bugForm.description}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-s) var(--spacing-xl)',
                  fontSize: 'var(--font-size-button)',
                  fontWeight: 'var(--button-font-weight)',
                  fontFamily: 'var(--font-family-primary)',
                  borderRadius: 'var(--button-radius)',
                  border: 'none',
                  cursor: (loading || !bugForm.title || !bugForm.description) ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-all)',
                  boxShadow: 'var(--shadow-sm)',
                  background: 'var(--Red-600)',
                  color: 'var(--Text-Contrast)',
                  opacity: (loading || !bugForm.title || !bugForm.description) ? 0.6 : 1
                }}
                onMouseEnter={(e) => !(loading || !bugForm.title || !bugForm.description) && (e.currentTarget.style.background = 'var(--Red-700)', e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = '0 4px 12px rgba(196, 100, 109, 0.25)')}
                onMouseLeave={(e) => !(loading || !bugForm.title || !bugForm.description) && (e.currentTarget.style.background = 'var(--Red-600)', e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    Submit Bug Report
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Suggestion Form */}
      {activeTab === 'suggestion' && (
        <div style={{
          background: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xl)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)'
        }}>
          <form onSubmit={handleSuggestionSubmit}>
            {/* Form Title */}
            <h3 style={{
              fontSize: 'var(--font-size-subtitle2)',
              fontWeight: 'var(--font-weight-subtitle2)',
              color: 'var(--Text-Primary)',
              margin: '0 0 var(--spacing-xl) 0',
              paddingBottom: 'var(--spacing-m)',
              borderBottom: '2px solid var(--Stroke-Light)'
            }}>
              Your Suggestion
            </h3>
            
            {/* Suggestion Title */}
            <div style={{ marginBottom: 'var(--spacing-l)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Suggestion Title <span style={{ color: 'var(--Red-600)' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Brief summary of your idea"
                value={suggestionForm.title}
                onChange={(e) => setSuggestionForm({ ...suggestionForm, title: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-s)',
                  fontSize: 'var(--font-size-body3)',
                  fontFamily: 'var(--font-family-primary)',
                  color: 'var(--Text-Primary)',
                  background: 'var(--Surface-Primary)',
                  border: '1px solid var(--Stroke-Secondary)',
                  borderRadius: 'var(--radius-s)',
                  transition: 'var(--transition-all)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--Purple-500)';
                  e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--Stroke-Secondary)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Detailed Description */}
            <div style={{ marginBottom: 'var(--spacing-l)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Detailed Description <span style={{ color: 'var(--Red-600)' }}>*</span>
              </label>
              <textarea
                placeholder="Explain your suggestion in detail. How would this improve the platform?"
                rows={7}
                value={suggestionForm.description}
                onChange={(e) => setSuggestionForm({ ...suggestionForm, description: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-s)',
                  fontSize: 'var(--font-size-body3)',
                  fontFamily: 'var(--font-family-primary)',
                  color: 'var(--Text-Primary)',
                  background: 'var(--Surface-Primary)',
                  border: '1px solid var(--Stroke-Secondary)',
                  borderRadius: 'var(--radius-s)',
                  transition: 'var(--transition-all)',
                  resize: 'vertical',
                  minHeight: '150px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--Purple-500)';
                  e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--Stroke-Secondary)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Two Column Row - Category/Priority */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-l)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Category <span style={{ color: 'var(--Red-600)' }}>*</span>
                </label>
                <select
                  value={suggestionForm.category}
                  onChange={(e) => setSuggestionForm({ ...suggestionForm, category: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2373737D\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right var(--spacing-s) center',
                    backgroundSize: '20px',
                    paddingRight: 'calc(var(--spacing-s) * 3)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="feature">New Feature</option>
                  <option value="improvement">General Improvement</option>
                  <option value="ui">Page layout/design</option>
                  <option value="performance">Performance</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Priority (from your perspective)
                </label>
                <select
                  value={suggestionForm.priority}
                  onChange={(e) => setSuggestionForm({ ...suggestionForm, priority: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2373737D\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right var(--spacing-s) center',
                    backgroundSize: '20px',
                    paddingRight: 'calc(var(--spacing-s) * 3)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="low">Low - Nice to have</option>
                  <option value="medium">Medium - Would be helpful</option>
                  <option value="high">High - Really need this</option>
                </select>
              </div>
            </div>

            {/* User Info Display */}
            <div style={{
              background: 'var(--Grey-50)',
              border: '1px solid var(--Stroke-Light)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-l)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Secondary)',
                margin: '0 0 var(--spacing-s) 0',
                textTransform: 'uppercase',
                letterSpacing: 'var(--letter-spacing-wide)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
                Submitting as:
              </p>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                margin: '0 0 var(--spacing-xxs) 0'
              }}>
                {jeweller?.companyName || 'Unknown Company'}
              </p>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                margin: 0
              }}>
                {jeweller?.email || 'No email'}
              </p>
            </div>

            {/* Submit Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--spacing-m)',
              paddingTop: 'var(--spacing-l)',
              borderTop: '1px solid var(--Stroke-Light)'
            }}>
              <button
                type="button"
                onClick={resetSuggestionForm}
                disabled={loading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-s) var(--spacing-xl)',
                  fontSize: 'var(--font-size-button)',
                  fontWeight: 'var(--button-font-weight)',
                  fontFamily: 'var(--font-family-primary)',
                  borderRadius: 'var(--button-radius)',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-all)',
                  boxShadow: 'var(--shadow-sm)',
                  background: 'var(--Button-Secondary-Enabled-BG)',
                  color: 'var(--Button-Secondary-Text)',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'var(--Button-Secondary-Hovered-BG)', e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'var(--Button-Secondary-Enabled-BG)', e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
              >
                Clear Form
              </button>
              <button
                type="submit"
                disabled={loading || !suggestionForm.title || !suggestionForm.description}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-s) var(--spacing-xl)',
                  fontSize: 'var(--font-size-button)',
                  fontWeight: 'var(--button-font-weight)',
                  fontFamily: 'var(--font-family-primary)',
                  borderRadius: 'var(--button-radius)',
                  border: 'none',
                  cursor: (loading || !suggestionForm.title || !suggestionForm.description) ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-all)',
                  boxShadow: 'var(--shadow-sm)',
                  background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                  color: 'var(--Grey-900)',
                  opacity: (loading || !suggestionForm.title || !suggestionForm.description) ? 0.6 : 1
                }}
                onMouseEnter={(e) => !(loading || !suggestionForm.title || !suggestionForm.description) && (e.currentTarget.style.background = 'linear-gradient(135deg, #FDE68A 0%, #FCD34D 100%)', e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.25)')}
                onMouseLeave={(e) => !(loading || !suggestionForm.title || !suggestionForm.description) && (e.currentTarget.style.background = 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      border: '2px solid rgba(0, 0, 0, 0.2)',
                      borderTopColor: 'var(--Grey-900)',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="btn-icon" />
                    Submit Suggestion
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;